import React, { useEffect, useRef, useState, useContext } from 'react';
import { compose } from 'redux';
import {
  withPauseTimer,
  withResumeTimer,
  withTriggerTimerVisibility,
} from '../hocs/player';
import BlueBookButton from 'chameleon/packages/blue-book-player/library/bluebook-button/BlueBookButton';
import Icon from 'chameleon/packages/blue-book-player/library/icon';
import BlueBookTimer from './BlueBookTimer';
import TimeoutModal from 'chameleon/packages/blue-book-player/library/modal/TimeoutModal/TimeoutModal';
import { IS_HEADER_PAUSE_BUTTON_VISIBLE } from 'chameleon/packages/blue-book-player/constants/componentVisibility';
import { BluebookContext } from 'chameleon/packages/blue-book-player/state/BluebookContext';
import {
  SET_IS_SUBMIT_TIMEOUT_MODAL_VISIBLE,
  SET_IS_AUTOSUBMITTED_BY_TIMER,
} from 'chameleon/packages/blue-book-player/state/reducer';
import { connect } from 'react-redux';

const TIMER_SHOW = 'Show';
const TIMER_HIDE = 'Hide';

const Countdown = (props) => {
  const [isPaused, setIsPaused] = useState(false);
  const [showTimer, setShowTimer] = useState(true);
  const [showTimerTriggerButton, setTimerTriggerButton] = useState(true);
  const [triggerTimerButtonName, setTriggerTimerButtonName] =
    useState(TIMER_HIDE);
  const [pauseTimeUsed, setPauseTimeUsed] = useState(0.0);
  const timerRef = useRef(null);

  const {
    userData,
    assignmentPlayer,
    quizTitle,
    pauseTimer,
    resumeTimer,
    triggerTimerVisibility,
    assignmentId,
    timerInfo,
    checkTimeExtension,
    allowHardTimerFF,
  } = props;
  const {
    dispatch,
    state: { assignment, isSubmitTimeOutModalVisbleOnce },
  } = useContext(BluebookContext);
  useEffect(() => {
    if (assignmentPlayer?.timedSession?.isTimerPaused) {
      setIsPaused(true);
    }
    if (assignmentPlayer?.timedSession?.isTimerHidden) {
      setShowTimer(false);
      setTriggerTimerButtonName(TIMER_SHOW);
    }
  }, [assignmentPlayer]);

  const buttonsClassName =
    'text-12 !font-bold w-[50px] leading-[16px] h-[20px] !bg-bluebook-blue1 hover:!bg-bluebook-blue2';

  useEffect(() => {
    if (!isPaused) {
      timerRef?.current?.childRef?.current?.focus();
    }
  }, [isPaused]);

  const pauseTest = (event) => {
    pauseTimer({
      variables: {
        assignmentId,
      },
    }).then((response) => {
      if (response.data.pauseTimer.ok) {
        setIsPaused(true);
        event?.currentTarget?.blur();
        setPauseTimeUsed(response?.data?.pauseTimer?.elapsedTime);
      }
    });
  };

  const resumeTest = (event) => {
    resumeTimer({
      variables: {
        assignmentId,
      },
    }).then((response) => {
      if (response.data.resumeTimer.ok) {
        setIsPaused(false);
        event?.currentTarget?.blur();
      }
    });
  };

  const showTimerTrigger = (event) => {
    triggerTimerVisibility({
      variables: {
        assignmentId,
        isTimerHidden: showTimer,
      },
    }).then((response) => {
      const isTimerHidden =
        response?.data?.triggerTimerVisibility?.isTimerHidden;
      isTimerHidden
        ? setTriggerTimerButtonName(TIMER_SHOW)
        : setTriggerTimerButtonName(TIMER_HIDE);
      setShowTimer(!isTimerHidden);
      event?.currentTarget?.blur();
    });
  };

  const handleSetIsTimeExpireModalVisible = () => {
    // Trigger auto-submission when timer expired
    if (assignment.isHardTimer) {
      dispatch({ type: SET_IS_AUTOSUBMITTED_BY_TIMER, payload: true });
    } else if (!isSubmitTimeOutModalVisbleOnce) {
      /**
       * Previously, for soft timer assignments this dispatch was called every
       * second after the timer gets to 0 which caused UI-related issues.
       *
       * isSubmitTimeOutModalVisbleOnce is a value that is set to true when a student
       * received the soft timer modal and dismisses it.
       *
       * By using isSubmitTimeOutModalVisbleOnce in this condition we ensure that this
       * dispatch is called only once.
       */
      dispatch({ type: SET_IS_SUBMIT_TIMEOUT_MODAL_VISIBLE, payload: true });
    }
  };

  return (
    <>
      <div
        className="flex flex-col justify-center items-center bb-mobile:flex-row"
        data-cy="countdown"
      >
        <BlueBookTimer
          userData={userData}
          showTimer={showTimer}
          isPaused={isPaused}
          resumeTest={resumeTest}
          setTimerTriggerButton={setTimerTriggerButton}
          assignmentPlayer={assignmentPlayer}
          quizTitle={quizTitle}
          setShowTimer={setShowTimer}
          ref={timerRef}
          pauseTimeUsed={pauseTimeUsed}
          triggerTimerVisibility={triggerTimerVisibility}
          assignmentId={assignmentId}
          timerInfo={timerInfo}
          checkTimeExtension={checkTimeExtension}
          data-test-id="bluebook-timer"
          data-cy="bluebook-timer"
          onCountdownEnd={handleSetIsTimeExpireModalVisible}
        />
        {!showTimer && (
          <div
            className="bb-mobile:absolute bb-mobile:left-4 h-[28px] text-center flex items-center"
            aria-hidden
          >
            <Icon
              id="timer_icon"
              className="align-middle"
              data-test-id="hide-timer-icon"
              data-cy="hide-timer-icon"
            />
          </div>
        )}
        <div className="bb-desktop:mt-[8px] flex w-full text-center justify-center gap-[8px]">
          {IS_HEADER_PAUSE_BUTTON_VISIBLE && (
            <BlueBookButton
              isBlack
              className={buttonsClassName}
              onClick={pauseTest}
              data-test-id="pause-button"
              data-cy="pause-button"
            >
              Pause
            </BlueBookButton>
          )}
          {showTimerTriggerButton && (
            <BlueBookButton
              isBlack
              className={buttonsClassName}
              onClick={showTimerTrigger}
              data-test-id="timer-trigger-button"
              data-cy="timer-trigger-button"
            >
              {triggerTimerButtonName}
            </BlueBookButton>
          )}
        </div>
      </div>
      <TimeoutModal
        allowHardTimerFF={allowHardTimerFF}
        data-test-id="timeout-modal"
      />
    </>
  );
};

export default compose(
  withPauseTimer,
  withResumeTimer,
  withTriggerTimerVisibility,
  connect(({ featureFlag }) => ({
    allowHardTimerFF: featureFlag?.updated_timer_controls,
  })),
)(Countdown);
